import * as React from 'react';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import JMESParser from '../../util/JMESParser';
import JSONDisplay from '../SubjectViewer/common/JMESDisplay/Explorer/JSONDisplay';

const PARSE_ERROR = Symbol('JMESPath parse error');

export interface JMESPathPreconditionPreviewProps {
  expression: string;
  schema: Record<string, unknown>;
  example: Record<string, unknown>;
  onChange: (expression: string) => void;
  onValidityChange?: (isValid: boolean) => void;
  disabled?: boolean;
}

export interface JMESPathEvaluation {
  error: string | null;
  isMatched: boolean | null;
  result: unknown;
}

const isPythonTruthy = (value: unknown): boolean => {
  if (value === null || value === undefined || value === false) {
    return false;
  }
  if (typeof value === 'number') {
    return value !== 0 && !Number.isNaN(value);
  }
  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  return Boolean(value);
};

export const evaluateJMESPathPrecondition = (
  expression: string,
  example: Record<string, unknown>,
): JMESPathEvaluation => {
  if (!expression.trim()) {
    return {
      error: null,
      isMatched: null,
      result: null,
    };
  }

  const parser = new JMESParser(
    example,
    expression,
    undefined,
    undefined,
    (() => PARSE_ERROR) as any,
  );
  const result = parser.untypedParse();

  if (result === PARSE_ERROR) {
    return {
      error: 'Unable to evaluate this JMESPath expression.',
      isMatched: null,
      result: null,
    };
  }

  return {
    error: null,
    isMatched: isPythonTruthy(result),
    result,
  };
};

const JMESPathPreconditionPreview: React.FC<
  JMESPathPreconditionPreviewProps
> = ({
  expression,
  schema,
  example,
  onChange,
  onValidityChange,
  disabled = false,
}) => {
  const evaluation = React.useMemo(
    () => evaluateJMESPathPrecondition(expression, example),
    [example, expression],
  );

  React.useEffect(() => {
    onValidityChange?.(!evaluation.error);
  }, [evaluation.error, onValidityChange]);

  return (
    <Box direction="column" gap={1}>
      <TextArea
        fullWidth
        rows={3}
        label="Static precondition (optional)"
        value={expression}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        helpMessage="JMESPath expression evaluated before the LLM condition."
      />

      <Text weight="bold">Event shape</Text>
      <JSONDisplay
        displayInput={schema}
        invalidIntlMessage={{
          id: 'jmesPathPreconditionPreview.schemaUnavailable',
          defaultMessage: 'Event shape unavailable.',
        }}
      />

      <Text weight="bold">Representative event</Text>
      <Text size="caption">
        This preview uses a representative example. Runtime events may contain
        different values, and real events may omit optional fields.
      </Text>
      <JSONDisplay
        displayInput={example}
        invalidIntlMessage={{
          id: 'jmesPathPreconditionPreview.exampleUnavailable',
          defaultMessage: 'Representative event unavailable.',
        }}
      />

      <Text weight="bold">Live result</Text>
      {!expression.trim() ? (
        <Text>No static precondition will be applied.</Text>
      ) : evaluation.error ? (
        <Text color="negative" role="alert">
          {evaluation.error}
        </Text>
      ) : (
        <>
          <Pill
            color={evaluation.isMatched ? 'positive' : 'default'}
            label={evaluation.isMatched ? 'Matched' : 'Not matched'}
          />
          <JSONDisplay
            displayInput={evaluation.result}
            invalidIntlMessage={{
              id: 'jmesPathPreconditionPreview.noResult',
              defaultMessage: 'Expression returned no result.',
            }}
          />
        </>
      )}
    </Box>
  );
};

export default JMESPathPreconditionPreview;
